import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./css/salemod.css";
import { apiPut, apiGet } from "../../../utils/apiClient";
import {
  formatDateToInput,
  parseTimeToDate,
  formatTimeToString,
  calculateEndDateTime,
} from "../../../utils/commonService";

const getTimeFromDateTime = (dateTime) => {
  if (!dateTime) return "";
  const date = new Date(dateTime);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const SaleMod = ({ sale, onClose, onRefresh }) => {
  const originalPrice = sale.originalPrice;

  const [discountType, setDiscountType] = useState(sale.saleType);
  const [discountRate, setDiscountRate] = useState(
    sale.saleType === "할인률"
      ? sale.saleValue
      : Math.round(((originalPrice - sale.discountedPrice) / originalPrice) * 100),
  );
  const [discountPrice, setDiscountPrice] = useState(sale.discountedPrice);
  const [startDate, setStartDate] = useState(formatDateToInput(sale.startDateTime));
  const [startTime, setStartTime] = useState(getTimeFromDateTime(sale.startDateTime));
  const [endDateTime, setEndDateTime] = useState("");
  const [saleTimeout, setSaleTimeout] = useState(24);

  useEffect(() => {
    const fetchTimeout = async () => {
      try {
        const response = await apiGet("/store/sale/time");
        if (response.ok) {
          const limitTime = await response.json();
          setSaleTimeout(limitTime);
        }
      } catch (error) {
        console.error("할인 제한 시간 조회 실패:", error);
      }
    };
    fetchTimeout();
  }, []);

  useEffect(() => {
    if (startDate && startTime) {
      setEndDateTime(calculateEndDateTime(startDate, startTime, saleTimeout));
    }
  }, [startDate, startTime, saleTimeout]);

  const handleDateTimeChange = (date, time) => {
    if (date) setStartDate(date);
    if (time) setStartTime(time);
  };

  const handleDiscountRateChange = (e) => {
    const rate = Number(e.target.value);
    setDiscountRate(rate || "");
    if (rate) {
      let discounted = Math.floor((originalPrice * (100 - rate)) / 100);
      if (discounted < 1) discounted = 1;
      if (discounted > originalPrice) discounted = originalPrice;
      setDiscountPrice(discounted);
    } else {
      setDiscountPrice("");
    }
  };

  const handleDiscountPriceChange = (e) => {
    let val = e.target.value;

    if (val === "") {
      setDiscountPrice("");
      setDiscountRate("");
      return;
    }

    val = Number(val);

    if (val < 0) val = 0;
    if (val > originalPrice) val = originalPrice;

    setDiscountPrice(val);
    const newRate = Math.round(((originalPrice - val) / originalPrice) * 100);
    setDiscountRate(newRate);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || !startTime) {
      alert("할인 시작 날짜와 시간을 입력해주세요.");
      return;
    }

    const selectedStartDateTime = new Date(`${startDate}T${startTime}:00`);
    const currentDateTime = new Date();

    if (selectedStartDateTime < currentDateTime) {
      alert("할인 시작 시간은 현재 시간보다 이전일 수 없습니다.");
      return;
    }

    if (discountPrice === "" || discountPrice < 0) {
      alert("할인 판매가를 입력해주세요.");
      return;
    }

    if (discountType === "할인률" && (!discountRate || discountRate < 0 || discountRate > 100)) {
      alert("할인률은 0~100 사이의 값을 입력해주세요.");
      return;
    }

    try {
      const startDateTime = `${startDate}T${startTime}:00`;

      const requestData = {
        saleType: discountType,
        saleValue: discountType === "할인률" ? parseInt(discountRate) : (originalPrice - parseInt(discountPrice)),
        discountedPrice: parseInt(discountPrice),
        startDateTime: startDateTime,
      };

      const response = await apiPut(`/store/sale/${sale.saleIdx}`, requestData);

      if (response.ok) {
        alert("할인이 성공적으로 수정되었습니다.");
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "할인 수정에 실패했습니다.");
      }
    } catch (err) {
      console.error("할인 수정 실패:", err);
      alert("할인 수정 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="sale-mod-backdrop">
      <div className="sale-mod-wrap">
        <div className="sale-mod-header">할인 수정</div>

        <div className="sale-mod-body">
          <form className="sale-mod-form" onSubmit={handleSubmit}>
            <div className="sale-mod-type">
              <label className="sale-mod-type-label">할인 방식</label>
              <div className="sale-mod-radio">
                <label>
                  <input
                    type="radio"
                    name="discountType"
                    value="할인률"
                    checked={discountType === "할인률"}
                    onChange={(e) => setDiscountType(e.target.value)}
                  />
                  할인률
                </label>
                <label>
                  <input
                    type="radio"
                    name="discountType"
                    value="할인가"
                    checked={discountType === "할인가"}
                    onChange={(e) => setDiscountType(e.target.value)}
                  />
                  할인가
                </label>
              </div>
            </div>

            <div className="sale-mod-inputs-row">
              <div className="sale-mod-field">
                <label>할인률 (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={discountRate}
                  onChange={handleDiscountRateChange}
                  readOnly={discountType === "할인가"}
                  className={discountType === "할인가" ? "readonly" : ""}
                  placeholder="예: 10"
                />
              </div>

              <div className="sale-mod-field">
                <label>할인 판매가 (원)</label>
                <input
                  type="number"
                  min="0"
                  max={originalPrice}
                  value={discountPrice}
                  onChange={handleDiscountPriceChange}
                  readOnly={discountType === "할인률"}
                  className={discountType === "할인률" ? "readonly" : ""}
                  placeholder="예: 8000"
                />
              </div>
            </div>

            <div className="sale-mod-date">
              <label>할인 시작 일자</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleDateTimeChange(e.target.value, startTime)}
              />
              <label>할인 시작 시간</label>
              <DatePicker
                selected={parseTimeToDate(startTime)}
                onChange={(date) => {
                  const timeString = formatTimeToString(date);
                  handleDateTimeChange(startDate, timeString);
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="시간"
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                className="sale-mod-time-input"
                popperPlacement="right-start"
              />
              <small className="sale-mod-time-hint">
                ⓘ 시간은 30분 단위로 선택 가능합니다
              </small>
            </div>

            <div className="sale-mod-end">
              <label>할인 종료 일시</label>
              <input
                type="text"
                value={endDateTime}
                readOnly
                placeholder="자동 계산됨"
              />
              <small className="sale-mod-time-hint">
                ⓘ 할인 시작 일시에 따라 자동 계산됩니다
              </small>
            </div>

            <div className="sale-mod-buttons">
              <button type="submit" className="sale-mod-submit">
                수정
              </button>
              <button
                type="button"
                className="sale-mod-cancel"
                onClick={onClose}
              >
                취소
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaleMod;
